import { EffectCallback, useEffect, useRef } from 'react';

/**
 * For versions of React lower than 18 `useEffectOnce` works just like our
 * `useOnMount` hook and makes sure the provided effect is run only once.
 * In React 18 in `StrictMode` or in the upcoming concurrent mode a component
 * may be mounted more than once, so using `useOnMount` will no longer guarantee
 * that the effect will only run once. To still have a hook that guarantees that
 * an effect is run only once in React 18 `useEffectOnce` was introduced.
 * https://blog.ag-grid.com/avoiding-react-18-double-mount/
 */
export const useEffectOnce = (effect: EffectCallback) => {
  const destroyFunc = useRef<ReturnType<EffectCallback> | null>(null);
  const calledOnce = useRef(false);
  const renderAfterCalled = useRef(false);

  if (calledOnce.current) {
    renderAfterCalled.current = true;
  }

  useEffect(() => {
    if (calledOnce.current) {
      return;
    }

    calledOnce.current = true;
    destroyFunc.current = effect();

    return () => {
      if (!renderAfterCalled.current) {
        return;
      }

      if (typeof destroyFunc.current === 'function') {
        destroyFunc.current();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
